import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import {
  api,
  type CutappCatalog,
  type CutappConnection,
  type CutappDuplicatedSku,
  type CutappFieldOptions,
  type CutappPhotoMode,
} from '../services/api';
import {
  CUTAPP_FIELD_LABELS,
  CutappProductSyncModal,
  DEFAULT_CUTAPP_FIELD_OPTIONS,
  cutappPendenciaText,
  cutappPriceSyncBreakdown,
  cutappReportSummary,
  runCutappProductImport,
  unwrapCutappConnection,
} from '../components/shared/CutappProductSync';
import { PhotoOverwriteWarning } from '../components/shared/PhotoOverwriteWarning';
import { SellerPriceSourcePanel } from '../components/seller/SellerPriceSourcePanel';

// Campos, textos e aviso vermelho vivem em components/shared/CutappProductSync.tsx
// para que esta pagina e a caixa de sincronizacao em Produtos nunca divirjam.
const DEFAULT_FIELD_OPTIONS = DEFAULT_CUTAPP_FIELD_OPTIONS;
const FIELD_LABELS = CUTAPP_FIELD_LABELS;

/** Texto obrigatorio do plano, secao 11.4. */
const DUPLICATE_SKU_WARNING =
  'Estes SKUs existem em mais de um catalogo selecionado. Cada importacao vai sobrescrever um com o outro '
  + '- o catalogo de maior prioridade prevalece.';

function catalogName(catalog: CutappCatalog | undefined, id: string) {
  return catalog?.nome || catalog?.name || id;
}

function unwrap<T>(payload: any, keys: string[]): T | null {
  if (!payload) return null;
  for (const key of keys) {
    if (payload[key] != null) return payload[key] as T;
  }
  if (payload.data != null) return payload.data as T;
  return null;
}

function DuplicateSkuAlert({ skus, title }: { skus: CutappDuplicatedSku[]; title?: string }) {
  if (!skus?.length) return null;
  return (
    <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4">
      <p className="text-sm font-bold text-amber-900">
        ⚠️ {title || 'SKU duplicado entre catalogos selecionados'}
      </p>
      <p className="mt-1 text-sm text-amber-900">{DUPLICATE_SKU_WARNING}</p>
      <ul className="mt-3 max-h-40 space-y-1 overflow-y-auto text-xs text-amber-900">
        {skus.slice(0, 100).map((item) => (
          <li key={item.sku} className="rounded bg-white/70 px-2 py-1">
            <span className="font-mono font-semibold">{item.sku}</span>
            {' '}
            <span className="text-amber-800">({(item.catalogos || []).join(', ')})</span>
          </li>
        ))}
      </ul>
      {skus.length > 100 && <p className="mt-2 text-xs text-amber-800">+{skus.length - 100} SKU(s) nao listados.</p>}
    </div>
  );
}

export function CutAppIntegrationPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [syncingPrices, setSyncingPrices] = useState(false);

  const [connectionExists, setConnectionExists] = useState(false);
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [fieldOptions, setFieldOptions] = useState<Required<CutappFieldOptions>>(DEFAULT_FIELD_OPTIONS);
  const [photoMode, setPhotoMode] = useState<CutappPhotoMode>('skip');
  const [syncEnabled, setSyncEnabled] = useState(false);

  const [catalogs, setCatalogs] = useState<CutappCatalog[]>([]);
  const [selectedCatalogIds, setSelectedCatalogIds] = useState<string[]>([]);
  const [duplicatedSkus, setDuplicatedSkus] = useState<CutappDuplicatedSku[]>([]);
  const [testedAt, setTestedAt] = useState<string | null>(null);

  const [fieldsModalOpen, setFieldsModalOpen] = useState(false);
  const [report, setReport] = useState<any | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);

  const catalogById = useMemo(() => {
    const map = new Map<string, CutappCatalog>();
    for (const catalog of catalogs) map.set(String(catalog.id), catalog);
    return map;
  }, [catalogs]);

  const availableCatalogs = useMemo(
    () => catalogs.filter((catalog) => !selectedCatalogIds.includes(String(catalog.id))),
    [catalogs, selectedCatalogIds],
  );

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.getCutappConnection();
        const connection: CutappConnection | null = unwrapCutappConnection(res);
        if (connection) {
          setConnectionExists(true);
          setBaseUrl(connection.baseUrl || '');
          // O backend NUNCA devolve a chave (nem cifrada): so o booleano `hasApiKey`.
          // Por isso o campo da tela fica vazio e o placeholder avisa que ja existe uma
          // chave salva; preencher significa SUBSTITUIR.
          setApiKeyConfigured(connection.hasApiKey === true);
          setSelectedCatalogIds(Array.isArray(connection.catalogIds) ? connection.catalogIds.map(String) : []);
          setFieldOptions({ ...DEFAULT_FIELD_OPTIONS, ...(connection.fieldOptions || {}) });
          setPhotoMode(connection.photoMode === 'overwrite' ? 'overwrite' : 'skip');
          setSyncEnabled(!!connection.syncEnabled);
          setLastSyncAt(connection.lastSyncAt || null);
        }
      } catch {
        // conexao ainda nao criada: mantem o formulario vazio
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const buildPayload = () => ({
    ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
    catalogIds: selectedCatalogIds,
    fieldOptions,
    photoMode,
    syncEnabled,
  });

  const persist = async (overrides: Record<string, unknown> = {}, silent = false) => {
    const payload = { ...buildPayload(), ...overrides };
    setSaving(true);
    try {
      const res = connectionExists
        ? await api.updateCutappConnection(payload)
        : await api.createCutappConnection(payload);
      setConnectionExists(true);
      if (payload.apiKey) setApiKeyConfigured(true);
      const connection = unwrap<CutappConnection>(res, ['connection']);
      if (connection?.lastSyncAt !== undefined) setLastSyncAt(connection?.lastSyncAt || null);
      if (!silent) toast.success('Conexao com o Cut App salva.');
      return true;
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao salvar a conexao com o Cut App');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!apiKey.trim() && !apiKeyConfigured) { toast.error('Informe a chave de API do Cut App'); return; }
    setTesting(true);
    try {
      const res = await api.testCutappConnection({
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        catalogIds: selectedCatalogIds,
      });
      if (res?.success === false) throw new Error(res?.message || 'Nao foi possivel conectar ao Cut App');
      const foundCatalogs: CutappCatalog[] = Array.isArray(res?.catalogs) ? res.catalogs : [];
      setCatalogs(foundCatalogs);
      setDuplicatedSkus(Array.isArray(res?.skusDuplicados) ? res.skusDuplicados : []);
      setTestedAt(new Date().toLocaleString('pt-BR'));
      const validIds = new Set(foundCatalogs.map((catalog) => String(catalog.id)));
      setSelectedCatalogIds((current) => current.filter((id) => validIds.has(id)));
      toast.success(`Conexao ok: ${foundCatalogs.length} catalogo(s) encontrado(s).`);
    } catch (err: any) {
      setCatalogs([]);
      toast.error(err?.message || 'Erro ao testar a conexao com o Cut App');
    } finally {
      setTesting(false);
    }
  };

  const addCatalog = (id: string) => {
    setSelectedCatalogIds((current) => (current.includes(id) ? current : [...current, id]));
  };

  const removeCatalog = (id: string) => {
    setSelectedCatalogIds((current) => current.filter((item) => item !== id));
  };

  const moveCatalog = (index: number, direction: -1 | 1) => {
    setSelectedCatalogIds((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next;
    });
  };

  // Campos + fotos sao editados no mesmo modal usado na caixa de sincronizacao de
  // Produtos (Nucleo de Negocio). O aviso vermelho da secao 11.3 e disparado la
  // dentro, sempre que a opcao for alterada para sobrescrever.
  const handleSaveFieldOptions = async (fields: Required<CutappFieldOptions>, mode: CutappPhotoMode) => {
    setFieldOptions(fields);
    setPhotoMode(mode);
    const ok = await persist({ fieldOptions: fields, photoMode: mode });
    if (ok) setFieldsModalOpen(false);
  };

  const handleImport = async () => {
    if (selectedCatalogIds.length === 0) {
      toast.error('Selecione ao menos um catalogo de origem');
      return;
    }
    const saved = await persist({}, true);
    if (!saved) return;
    setImporting(true);
    setReport(null);
    try {
      const result = await runCutappProductImport(selectedCatalogIds);
      setReport(result);
      setLastSyncAt(new Date().toISOString());
      toast.success(cutappReportSummary(result));
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao importar produtos do Cut App');
    } finally {
      setImporting(false);
    }
  };

  const handleSyncPrices = async () => {
    setSyncingPrices(true);
    try {
      const res = await api.runCutappSyncPrices();
      const result = unwrap<any>(res, ['report', 'result']) || res;
      setReport(result);
      toast.success(`Precos sincronizados: ${result?.updated || 0} linha(s) atualizada(s). ${cutappPriceSyncBreakdown(result)}`.trim(), { duration: 8000 });
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao sincronizar precos do Cut App');
    } finally {
      setSyncingPrices(false);
    }
  };

  const connected = connectionExists && (apiKeyConfigured || !!apiKey.trim());
  const pendencias: any[] = Array.isArray(report?.pendencias) ? report.pendencias : [];
  const skusSobrescritos: CutappDuplicatedSku[] = Array.isArray(report?.skusSobrescritos)
    ? report.skusSobrescritos.map((item: any) => (typeof item === 'string' ? { sku: item, catalogos: [] } : item))
    : [];

  return (
    <>
      <Header title="Cut App" subtitle="Importacao de produtos e origem dos precos por marketplace" />

      <div className="p-6">
        <div className="mx-auto max-w-5xl space-y-6">
          {/* Conexao */}
          <div className="rounded-lg bg-white p-6 shadow">
            <div className="mb-6 flex items-center space-x-4">
              <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white">
                <img src="/cutapp.svg" alt="Cut App" className="h-9 w-9 object-contain" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Conexao com o Cut App</h2>
                <p className="text-sm text-gray-500">
                  O Cut App e a fonte: a Integraia apenas le produtos e precos (somente leitura). Gere a chave em
                  Configuracoes &rarr; Integracoes &rarr; Integraia, dentro do Cut App.
                </p>
              </div>
              <div className="ml-auto">
                <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${connected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {connected ? '● Conectado' : '○ Nao configurado'}
                </span>
              </div>
            </div>

            {loading ? (
              <p className="text-sm text-gray-500">Carregando conexao...</p>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-2">
                  {/* URL e do sistema, nao do tenant: vem da env CUTAPP_BASE_URL do
                      backend e e exibida apenas como informacao. Nao e editavel. */}
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-700">Servidor do Cut App</label>
                    <div className="flex h-[38px] w-full items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-600">
                      {baseUrl || 'Definido pelo sistema'}
                    </div>
                    <p className="mt-1 text-xs text-gray-400">Configurado no ambiente da Integraia, igual para todos.</p>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-700">Chave de API</label>
                    <input
                      type="password"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder={apiKeyConfigured ? 'Chave configurada - preencha para substituir' : 'cutp_...'}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleTest}
                    disabled={testing}
                    className="rounded-md bg-purple-600 px-5 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
                  >
                    {testing ? 'Testando...' : 'Testar conexao'}
                  </button>
                  <button
                    type="button"
                    onClick={() => persist()}
                    disabled={saving}
                    className="rounded-md border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {saving ? 'Salvando...' : 'Salvar conexao'}
                  </button>
                </div>
                <p className="text-xs text-gray-400">🔒 A chave e cifrada e fica armazenada no servidor.</p>
                {testedAt && (
                  <p className="text-xs text-gray-500">
                    Ultimo teste: {testedAt} - {catalogs.length} catalogo(s) encontrado(s).
                  </p>
                )}
                {lastSyncAt && (
                  <p className="text-xs text-gray-500">Ultima sincronizacao: {new Date(lastSyncAt).toLocaleString('pt-BR')}</p>
                )}
              </div>
            )}
          </div>

          {/* Catalogos ordenados */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h3 className="text-sm font-semibold text-gray-900">Catalogos de origem (a ordem e a prioridade)</h3>
            <p className="mt-1 text-xs leading-5 text-gray-500">
              O casamento de produto e sempre por <strong>SKU</strong>. Se o mesmo SKU existir em mais de um catalogo
              selecionado, um sobrescreve o outro seguindo esta lista: <strong>o ultimo da lista prevalece</strong>.
              Reordene para decidir quem ganha - sem ordem definida o conteudo do produto mudaria de uma execucao para
              a outra.
            </p>

            {catalogs.length === 0 ? (
              <div className="mt-4 rounded-md border border-dashed border-gray-300 px-3 py-6 text-center text-sm text-gray-500">
                Teste a conexao para listar os catalogos do Cut App.
              </div>
            ) : (
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Catalogos disponiveis</p>
                  <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border border-gray-200 p-2">
                    {availableCatalogs.length === 0 && (
                      <p className="px-2 py-3 text-xs text-gray-500">Todos os catalogos ja estao selecionados.</p>
                    )}
                    {availableCatalogs.map((catalog) => (
                      <button
                        key={catalog.id}
                        type="button"
                        onClick={() => addCatalog(String(catalog.id))}
                        className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 px-3 py-2 text-left text-sm text-gray-800 hover:border-purple-300 hover:bg-purple-50"
                      >
                        <span className="truncate">{catalogName(catalog, String(catalog.id))}</span>
                        <span className="shrink-0 text-xs font-semibold text-purple-700">Adicionar &rarr;</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Selecionados em ordem de prioridade
                  </p>
                  <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border border-gray-200 p-2">
                    {selectedCatalogIds.length === 0 && (
                      <p className="px-2 py-3 text-xs text-gray-500">Nenhum catalogo selecionado.</p>
                    )}
                    {selectedCatalogIds.map((id, index) => {
                      const isLast = index === selectedCatalogIds.length - 1;
                      return (
                        <div
                          key={id}
                          className={`rounded-md border px-3 py-2 ${isLast ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-white'}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-900 text-xs font-bold text-white">
                              {index + 1}
                            </span>
                            <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
                              {catalogName(catalogById.get(id), id)}
                            </span>
                            <div className="flex shrink-0 items-center gap-1">
                              <button type="button" onClick={() => moveCatalog(index, -1)} disabled={index === 0} title="Subir (menos prioridade)" className="rounded border border-gray-300 px-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-30">▲</button>
                              <button type="button" onClick={() => moveCatalog(index, 1)} disabled={isLast} title="Descer (mais prioridade)" className="rounded border border-gray-300 px-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-30">▼</button>
                              <button type="button" onClick={() => removeCatalog(id)} title="Remover" className="rounded border border-red-200 px-1.5 text-xs font-semibold text-red-600 hover:bg-red-50">✕</button>
                            </div>
                          </div>
                          <p className="mt-1 pl-8 text-[11px] font-semibold uppercase tracking-wide">
                            {isLast
                              ? <span className="text-emerald-700">Maior prioridade - prevalece sobre os demais</span>
                              : <span className="text-gray-400">Pode ser sobrescrito pelos de baixo</span>}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                  {selectedCatalogIds.length > 1 && (
                    <p className="mt-2 text-xs font-semibold text-gray-700">
                      Prevalece: {catalogName(catalogById.get(selectedCatalogIds[selectedCatalogIds.length - 1]), selectedCatalogIds[selectedCatalogIds.length - 1])}
                    </p>
                  )}
                </div>
              </div>
            )}

            {duplicatedSkus.length > 0 && (
              <div className="mt-4">
                <DuplicateSkuAlert skus={duplicatedSkus} />
              </div>
            )}
          </div>

          {/* Opt-in por campo + fotos: mesmo modal usado em Produtos */}
          <div className="rounded-lg bg-white p-6 shadow">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">O que o Cut App pode gravar no produto</h3>
                <p className="mt-1 text-xs text-gray-500">
                  Cada campo marcado passa a ser de posse desta conexao: os syncs de Shopee/Mercado Livre nao escrevem nele.
                  Campos desmarcados ficam intactos. Esta e a mesma configuracao da caixa de sincronizacao em
                  Nucleo de Negocio &rarr; Produtos.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFieldsModalOpen(true)}
                className="rounded-md border border-purple-200 bg-purple-50 px-4 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-100"
              >
                Configurar campos e fotos
              </button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {FIELD_LABELS.map((field) => (
                <span
                  key={field.key}
                  title={field.desc}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${fieldOptions[field.key] ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-400 line-through'}`}
                >
                  {field.title}
                </span>
              ))}
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${photoMode === 'overwrite' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                Fotos: {photoMode === 'overwrite' ? 'sobrescrever' : 'nao trazer'}
              </span>
            </div>
            {photoMode === 'overwrite' && (
              <div className="mt-4">
                <PhotoOverwriteWarning />
              </div>
            )}
          </div>

          {/* Sync + importar */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h3 className="text-sm font-semibold text-gray-900">Sincronizacao</h3>
            <label className="mt-3 flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-3 text-sm text-gray-700">
              <span>
                <span className="block font-semibold text-gray-800">Sincronizacao automatica diaria</span>
                <span className="block text-xs text-gray-500">Entra no agendador que ja roda os syncs de produto (1x/dia) e o de precos (a cada 4h).</span>
              </span>
              <input
                type="checkbox"
                checked={syncEnabled}
                onChange={(e) => { setSyncEnabled(e.target.checked); persist({ syncEnabled: e.target.checked }, true); }}
                className="h-4 w-4 accent-purple-600"
              />
            </label>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleImport}
                disabled={importing || saving || selectedCatalogIds.length === 0}
                className="rounded-md bg-gray-900 px-5 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {importing ? 'Importando...' : 'Importar agora'}
              </button>
              <button
                type="button"
                onClick={handleSyncPrices}
                disabled={syncingPrices || !fieldOptions.prices}
                title={fieldOptions.prices ? undefined : 'Marque "Precos" no opt-in por campo para sincronizar precos'}
                className="rounded-md border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {syncingPrices ? 'Sincronizando precos...' : 'Sincronizar precos agora'}
              </button>
            </div>

            {report && (
              <div className="mt-5 space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="rounded-md bg-emerald-50 px-2 py-2 text-emerald-700"><div className="text-base font-bold">{report.created || 0}</div><div>Criados</div></div>
                  <div className="rounded-md bg-blue-50 px-2 py-2 text-blue-700"><div className="text-base font-bold">{report.updated || 0}</div><div>Atualizados</div></div>
                  <div className="rounded-md bg-gray-50 px-2 py-2 text-gray-600"><div className="text-base font-bold">{report.skipped || 0}</div><div>Ignorados</div></div>
                </div>

                {skusSobrescritos.length > 0 && (
                  <DuplicateSkuAlert
                    skus={skusSobrescritos}
                    title={`${skusSobrescritos.length} SKU(s) foram sobrescritos nesta importacao`}
                  />
                )}

                {pendencias.length > 0 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                    <p className="text-sm font-semibold text-amber-900">{pendencias.length} pendencia(s)</p>
                    <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto text-xs text-amber-900">
                      {pendencias.slice(0, 100).map((item, index) => (
                        <li key={index} className="rounded bg-white/70 px-2 py-1">
                          {cutappPendenciaText(item)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mapeamento de precos: vive aqui, dentro da conexao, e nao em uma aba
              escondida do Seller. Sem conexao salva nao ha o que mapear. */}
          {connected ? (
            <SellerPriceSourcePanel />
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
              Salve a conexao com o Cut App para configurar o mapeamento de precos.
            </div>
          )}
        </div>
      </div>

      {fieldsModalOpen && (
        <CutappProductSyncModal
          initialFields={fieldOptions}
          initialPhotoMode={photoMode}
          saving={saving}
          saveLabel="Salvar configuracao"
          onClose={() => setFieldsModalOpen(false)}
          onSave={handleSaveFieldOptions}
        />
      )}
    </>
  );
}
